import React, { createContext, useContext } from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  CartScope,
  DRAFT_CART_SCOPE,
  MAIN_CART_SCOPE,
} from '../../redux/features/draftCart/cartScope';
import { selectSelectedItemCartScope } from '../../selectors/dialog';

/**
 * HITL-1614 — which cart a rendered row belongs to.
 *
 * The vocabulary itself moved to `redux/features/draftCart/cartScope.ts` in
 * HITL-1617, once `dialogSlice` and two thunks needed the same three names and a
 * reducer importing them out of a `.tsx` file stopped being defensible.
 * Re-exported here so every existing import of `CartScope` / `MAIN_CART_SCOPE` /
 * `DRAFT_CART_SCOPE` from this module keeps working unchanged.
 */
export type { CartScope };
export { MAIN_CART_SCOPE, DRAFT_CART_SCOPE };

/**
 * Defaults to `main`, which is what makes this change invisible with the flag
 * off: every existing row renders outside any provider and therefore keeps
 * behaving exactly as it does today.
 */
const CartScopeContext = createContext<CartScope>(MAIN_CART_SCOPE);

export const CartScopeProvider = CartScopeContext.Provider;

export const useCartScope = (): CartScope => useContext(CartScopeContext);

/**
 * THE id-collision fix.
 *
 * `dialogSlice.selectedItemCartId` is a single number over ONE id space, and
 * both slices number `cartItemId` from 1 (`sequenceId` starts fresh in each).
 * Six components compare it bare against `cartItem.cartItemId`
 * (`CartItemModGroups`, `CartItemChildModGroup`, `CartItemReview` twice,
 * `RecursiveModGroup`, `ItemBuilder`), so the moment both columns are on screen
 * selecting main-cart item 2 ALSO lights up draft item 2: two selected borders,
 * `scrollIntoView` firing in both columns, and a My Cart click opening the main
 * cart's item in the centre pane.
 *
 * HITL-1614 answered the half it could: a draft row never matched, full stop,
 * because only the main cart could be the dialog's selection at all. The centre
 * pane's write path was still hard-wired to `cartSlice`, so making a draft row
 * selectable would have meant editing a draft item mutates the order.
 *
 * HITL-1617 routes that write path, so a draft row IS selectable now and the
 * comparison becomes the real one: the row's own scope against the scope the
 * dialog recorded when the selection was made. Two id spaces, two scopes, one
 * lit row.
 */
export const useIsCartItemSelected = (
  selectedItemCartId: number,
  cartItemId: number
): boolean => {
  const renderedScope = useCartScope();
  const selectedScope = useAppSelector(selectSelectedItemCartScope);
  return renderedScope === selectedScope && selectedItemCartId === cartItemId;
};

interface DraftCartScopeProps {
  children: React.ReactNode;
}

/** Marks a subtree as rendering `draftCart` rows. */
export const DraftCartScope = ({ children }: DraftCartScopeProps) => (
  <CartScopeProvider value={DRAFT_CART_SCOPE}>{children}</CartScopeProvider>
);

export default CartScopeContext;
